import React, { useState, useEffect, useRef, useImperativeHandle } from "react";
import {
  CreditCard,
  Banknote,
  QrCode,
  DollarSign,
  CheckCircle,
  Receipt,
  ArrowRight,
} from "lucide-react";
import { Modal, toaster, Message } from "rsuite";
import { guardarCobro } from "../Services/ConsultasServices";

export const ModalCobro = ({
  idNegocio,
  precioConsulta,
  idConsulta,
  paciente,
  servicio,
  onSendData,
  ref,
}) => {
  const [precio, setPrecio] = useState(precioConsulta);
  const [paymentMethod, setPaymentMethod] = useState("Efectivo"); // 'cash', 'card', 'transfer'
  const [amountReceived, setAmountReceived] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const initialized = useRef(false);

  // Cálculo del cambio para pago en efectivo
  const numericAmountReceived = parseFloat(amountReceived) || 0;
  const change =
    paymentMethod === "Efectivo"
      ? Math.max(0, numericAmountReceived - precio)
      : 0;
  const isCashInsufficient =
    paymentMethod === "Efectivo" && numericAmountReceived < precio;

  const handleFocus = (event) => event.target.select();

  const toast = (msg, type = "info") => {
    toaster.push(
      <Message showIcon type={type} closable>
        <strong>{type}!</strong>
        &nbsp; {msg}
      </Message>,
      { duration: 5000, placement: "topCenter" },
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (paymentMethod === "cash" && isCashInsufficient) return;

    const paymentData = {
      paciente,
      servicio,
      amount: precio,
      paymentMethod,
      amountReceived: paymentMethod === "cash" ? numericAmountReceived : precio,
      change: paymentMethod === "Efectivo" ? change : 0,
      timestamp: new Date().toISOString(),
    };

    setIsCompleted(true);
    saveCobro();
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSendData = () => {
    onSendData("Ok");
		setIsCompleted(false);
    setIsOpen(false);
  };

  const saveCobro = async () => {
    const datos = {
      id: idNegocio,
      idConsulta: idConsulta,
      formaPago: paymentMethod,
      cobro: amountReceived,
    };

    await guardarCobro(datos)
      .then((res) => {
        if (res.data > 0) {
          toast("Se ha registrado el pago de forma correcta.", "success");
        } else {
          toast("Ocurrio un error al grabar el cobro", "error");
        }
      })
      .catch((err) => {
        toast(err.name, err.message);
      });
  };

  useImperativeHandle(
    ref,
    () => ({
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      toggle: () => setIsOpen((_) => !_),
    }),
    [],
  );

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      setAmountReceived(0);
      setIsCompleted(false);
    }
  }, []);

  useEffect(() => {}, [isCompleted]);

  return (
    <div>
      <Modal
        size="md"
        open={isOpen}
        onClose={handleClose}
        backdrop="static"
        overflow="false"
      >
        <Modal.Body>
          {isCompleted ? (
            <div>
              <div className="w-full h-full min-h-full bg-slate-100 p-6">
                <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-10 h-10" />
                </div>
                <h2 className="text-2xl font-bold text-slate-800">
                  ¡Cobro Exitoso!
                </h2>
                <p className="text-sm text-slate-600">
                  El pago de{" "}
                  <span className="font-semibold text-slate-800">
                    ${precioConsulta.toFixed(2)}
                  </span>{" "}
                  por concepto de{" "}
                  <span className="font-semibold text-slate-800">
                    {servicio}
                  </span>{" "}
                  ha sido registrado correctamente.
                </p>
                <div className="pt-4 flex flex-col gap-2">
                  <button
                    onClick={() => handleSendData()}
                    className="w-full py-3.5 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl flex items-center justify-center gap-2 shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className="w-full h-full min-h-full rounded-2xl bg-slate-100 p-6">
                {/* Encabezado */}
                <div className="bg-slate-900 text-white rounded-2xl p-6 flex items-center justify-between">
                  <div>
                    <h1 className="text-lg font-bold flex items-center gap-2">
                      <Receipt className="w-5 h-5 text-blue-400" />
                      Cobro de Consulta ({paciente})
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                      {servicio.toUpperCase()}
                    </p>
                  </div>
                  <span className="text-xs bg-blue-500/20 text-blue-300 font-mono px-3 py-1 rounded-full border border-blue-500/30">
                    Caja #1
                  </span>
                </div>

                {/* Formulario */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                  {/* Campo: Precio */}
                  <div>
                    <label className="block text-xs font-semibold uppercase text-slate-500 mb-1.5">
                      Precio de la Consulta
                    </label>
                    <div className="relative rounded-xl border border-slate-300 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all p-3 flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-slate-400 shrink-0" />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={precioConsulta}
                        onFocus={(e) => handleFocus(e)}
                        onChange={(e) => setPrecio(parseFloat(e.target.value))}
                        className="w-full text-lg font-bold text-slate-800 focus:outline-none"
                        placeholder="0.00"
                        required
                      />
                    </div>
                  </div>

                  {/* Campo: Forma de Pago */}
                  <div>
                    <label className="block text-xs font-semibold uppercase text-slate-500 mb-2">
                      Forma de Pago
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {/* Opción Efectivo */}
                      <button
                        type="button"
                        onClick={() => setPaymentMethod("Efectivo")}
                        className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all ${
                          paymentMethod === "Efectivo"
                            ? "border-blue-600 bg-blue-50 text-blue-700 font-semibold"
                            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <Banknote className="w-6 h-6 mb-1" />
                        <span className="text-xs">Efectivo</span>
                      </button>

                      {/* Opción Tarjeta */}
                      <button
                        type="button"
                        onClick={() => setPaymentMethod("Tarjeta")}
                        className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all ${
                          paymentMethod === "Tarjeta"
                            ? "border-blue-600 bg-blue-50 text-blue-700 font-semibold"
                            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <CreditCard className="w-6 h-6 mb-1" />
                        <span className="text-xs">Tarjeta</span>
                      </button>

                      {/* Opción Transferencia */}
                      <button
                        type="button"
                        onClick={() => setPaymentMethod("Transferencia")}
                        className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all ${
                          paymentMethod === "Transferencia"
                            ? "border-blue-600 bg-blue-50 text-blue-700 font-semibold"
                            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <QrCode className="w-6 h-6 mb-1" />
                        <span className="text-xs">Transferencia</span>
                      </button>
                    </div>
                  </div>

                  {/* Sección condicional: Cobro en Efectivo (Monto Recibido y Cambio) */}
                  {paymentMethod === "Efectivo" && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1">
                          Monto Recibido
                        </label>
                        <div className="relative rounded-lg border border-slate-300 bg-white p-2.5 flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-slate-400" />
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={amountReceived}
                            onFocus={(e) => handleFocus(e)}
                            onChange={(e) => setAmountReceived(e.target.value)}
                            placeholder="Monto entregado por el paciente"
                            className="w-full text-sm font-semibold text-slate-800 focus:outline-none"
                            required={paymentMethod === "Efectivo"}
                          />
                        </div>
                      </div>

                      {/* Mostrar Cambio */}
                      <div className="flex justify-between items-center pt-2 border-t border-slate-200 text-sm">
                        <span className="text-slate-600 font-medium">
                          Cambio a Entregar:
                        </span>
                        <span
                          className={`text-lg font-bold ${isCashInsufficient ? "text-amber-600" : "text-emerald-600"}`}
                        >
                          ${change.toFixed(2)}
                        </span>
                      </div>
                      {isCashInsufficient && numericAmountReceived > 0 && (
                        <p className="text-xs text-amber-600">
                          El monto ingresado es menor al total de la consulta.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Botón de Confirmación */}
                  <button
                    type="submit"
                    disabled={
                      paymentMethod === "Efectivo" && isCashInsufficient
                    }
                    className="w-full py-3.5 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl flex items-center justify-center gap-2 shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Procesar Cobro (${precio.toFixed(2)})
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </form>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!isCompleted ? (
            <button
              type="button"
              onClick={handleClose}
              className="w-100 px-4 py-2 bg-slate-100 text-sm font-bold text-slate-800 hover:bg-slate-300 rounded-xl transition-colors"
            >
              Cerrar
            </button>
          ) : (
            ""
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};
